import { Fragment, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { GenericSvg } from "../../assets/svgs/GenericSVG";
import { CustomButton } from "../../core/components/buttons/CustomButton";
import { InfoText } from "../../core/components/texts/InfoText";
import {
  getAppointmentPatientIsSelfPayer,
  getAppointmentSelfPayerDurationMinutes,
} from "../../core/store/appointmentSlice";
import { getCurrentPracticeId } from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { H4 } from "../../core/styles/stringStyles";
import { useStyles } from "../../core/styles/theme";
import { hexWithOpacity } from "../../core/utils/helpers";
import { useHandleParamNavigation } from "../../core/utils/useHandleParamNavigation";
import { AppointmentScreenWrapper } from "../onboarding/AppointmentScreenWrapper";
import {
  formatDurationOptionLabel,
  getSelfPayerCatalogue,
} from "./utils/selfPayerTreatmentOptions";

export const AppointmentSelfPayerDurationScreen = () => {
  const { t } = useTranslation();
  const theme = useStyles();
  const navigate = useHandleParamNavigation();
  const practiceId = useMainStore(getCurrentPracticeId);
  const { durations } = getSelfPayerCatalogue(practiceId);
  const isSelfPayer = useMainStore(getAppointmentPatientIsSelfPayer);
  const durationMinutes = useMainStore(getAppointmentSelfPayerDurationMinutes);
  const setDurationMinutes = useMainStore(
    (state) => state.setAppointmentSelfPayerDurationMinutes,
  );

  // The route stays reachable by URL and must not strand a patient who never chose self-payer.
  useEffect(() => {
    if (isSelfPayer) return;
    navigate("/appointment/recipeInfo", { replace: true });
  }, [isSelfPayer, navigate]);

  if (!isSelfPayer) return null;

  return (
    <AppointmentScreenWrapper nextButtonDisabled={!durationMinutes}>
      <InfoText
        headline={t("APPOINTMENT_SELF_PAYER_DURATION_HEADLINE")}
        text={[t("APPOINTMENT_SELF_PAYER_DURATION_DESCRIPTION")]}
      />
      <OptionsContainer>
        {durations.map((option, index) => {
          const isSelected = durationMinutes === option.minutes;
          return (
            <Fragment key={option.minutes}>
              <OptionButton onClick={() => setDurationMinutes(option.minutes)}>
                <GenericSvg
                  name={isSelected ? "CheckBoxRoundActive" : "CheckBoxRoundInactive"}
                  width={24}
                  height={24}
                  primaryColor={
                    isSelected
                      ? theme.colors.dark
                      : hexWithOpacity(theme.colors.dark, 0.3)
                  }
                />
                <OptionLabel>
                  {formatDurationOptionLabel(option.minutes, option.priceLabel)}
                </OptionLabel>
              </OptionButton>
              {index < durations.length - 1 && <Divider />}
            </Fragment>
          );
        })}
      </OptionsContainer>
    </AppointmentScreenWrapper>
  );
};

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const OptionButton = styled(CustomButton)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
`;

const OptionLabel = styled(H4)`
  color: ${(props) => props.theme.colors.dark};
  text-align: left;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) =>
    hexWithOpacity(props.theme.colors.medium, 0.1)};
  margin: 10px 0;
`;
